import type {DesignCenterTabProps} from "../Interfaces";
import {ChatRole} from "../../base/Settings";
import {BaseComponent, type BaseComponentState, BaseComponentStateInitial} from "../../base/ui_general/BaseComponent";
import React from "react";
import autobind from "autobind-decorator";
import DOMPurify from "dompurify";
import type {ChatMessage, ChatHistory} from "../../base/ui_general/GeneralInterfaces";
import {getRequest_post_chat} from "../../api/chat.tsx";
import "./ai-content.css";
import Plot from "react-plotly.js";
import styles from "./TabShared.module.css";

// Define optional block schemas for mixed content (HTML + plots)
type AIHtmlBlock = { kind: "html"; content: string };
type AIPlotlyBlock = { kind: "plotly"; data: any[]; layout?: any; config?: any };

interface AIBlocksMessage {
    blocks: Array<AIHtmlBlock | AIPlotlyBlock>
}

function tryParseJSONBlocks(text: string): AIBlocksMessage {
    const fallback: AIBlocksMessage = {blocks: [{kind: "html", content: text}]};
    const t = text.trim();
    if (!t.startsWith("{") && !t.startsWith("[")) return fallback;
    try {
        const obj = JSON.parse(t);
        // Case 1: already in blocks format
        if (obj && typeof obj === "object" && Array.isArray((obj as AIBlocksMessage).blocks)) {
            const rawBlocks = (obj as AIBlocksMessage).blocks;
            const blocks: Array<AIHtmlBlock | AIPlotlyBlock> = [];
            for (const b of rawBlocks as any[]) {
                if (b && b.kind === "html" && typeof b.content === "string") {
                    blocks.push({kind: "html", content: b.content});
                } else if (b && b.kind === "plotly" && Array.isArray(b.data)) {
                    blocks.push({kind: "plotly", data: b.data, layout: b.layout, config: b.config});
                }
            }
            return blocks.length ? {blocks} : fallback;
        }
        // Case 2: plain plotly object
        if (obj && typeof obj === "object" && (obj as any).type === "plotly" && Array.isArray((obj as any).data)) {
            const p = obj as any;
            return {blocks: [{kind: "plotly", data: p.data, layout: p.layout, config: p.config}]};
        }
        // Case 3: JSON string -> treat as HTML
        if (typeof obj === "string") {
            return {blocks: [{kind: "html", content: obj}]};
        }
        // Unknown JSON shape -> fallback to raw text as HTML
        return fallback;
    } catch {
        // Partial/invalid JSON while streaming -> fallback to raw text as HTML
        return fallback;
    }
}

/**
 * AIChatTab provides a simple chat interface to interact with the backend AI LLM.
 * It sends the user's prompt to the chat endpoint and renders streamed responses progressively.
 */

/**
 * Props: extend DesignCenterTabProps with chat_history provided by parent view.
 */
interface AIChatTabProps extends DesignCenterTabProps {
    chat_history: ChatHistory | undefined;
}

/**
 * State type for the AIChatTab component.
 */
interface AIChatTabState extends BaseComponentState {
    /** Chat conversation messages (user and assistant) */
    messages: ChatMessage[];
    /** Current text input value */
    input: string;
    /** Whether a message is currently being sent */
    isSending: boolean;
}

/**
 * AIChatTab component:
 * - Renders a chat window with user input.
 * - Sends prompts to the chat API using streaming helpers from BaseComponent.
 * - Updates assistant replies incrementally as the stream progresses.
 * - Routes network errors via displayMessage using BaseComponent.
 */
export class AIChatTab extends BaseComponent<AIChatTabProps, AIChatTabState> {
    /** Scroll container ref for auto-scrolling to latest messages */
    private listRef: React.RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();

    /**
     * Constructor initializes state with an empty conversation and input field.
     */
    constructor(props: AIChatTabProps) {
        super(props);
        this.state = {
            ...BaseComponentStateInitial,
            messages: props.chat_history?.messages ?? [],
            input: "",
            isSending: false,
        } as AIChatTabState;
    }


    /**
     * Lifecycle: after updates, scroll to the bottom when messages changed. Also sync messages when chat_history prop changes.
     */
    componentDidUpdate(prevProps: AIChatTabProps, prevState: AIChatTabState) {
        if (prevProps.chat_history !== this.props.chat_history) {
            const msgs = this.props.chat_history?.messages ?? [];
            this.setState({messages: msgs});
        }
        if (prevState.messages !== this.state.messages) {
            // auto scroll to bottom when messages change
            this.listRef.current?.scrollTo({top: this.listRef.current.scrollHeight, behavior: "smooth"});
        }
    }

    /**
     * Sends the current input as a chat message:
     * - Adds the user message and an assistant placeholder.
     * - Streams the assistant response and updates the placeholder text progressively.
     * - Uses BaseComponent.processFetchStreamRequest to call the chat endpoint.
     */
    @autobind
    async sendMessage() {
        const input = this.state.input.trim();
        if (!input) return;
        const userMsg: ChatMessage = {
            role: ChatRole.USER,
            message_text: input,
        };
        // Add user message and a placeholder assistant message once
        const assistantPlaceholder: ChatMessage = {
            role: ChatRole.ASSISTANT,
            message_text: "",
        };
        this.setState(prev => ({
            messages: [...prev.messages, userMsg, assistantPlaceholder],
            input: "",
            isSending: true
        }));

        // Accumulate assistant text and update UI incrementally via onChunk
        let assistantText = "";
        const onChunk = (chunk: string) => {
            assistantText += chunk;
            this.updateAssistantPartial(assistantText);
        };

        const fetchRequest = getRequest_post_chat({
            prompt: userMsg.message_text,
            transaction_id: this.props.transaction?.id
        });
        this.processFetchStreamRequest(fetchRequest, onChunk, () => {
            this.setState({isSending: false});
            this.props.loadData();
        });
    }

    /**
     * Updates the assistant placeholder message with the progressively received text.
     * Ensures only one assistant message is present, avoiding duplicates.
     */
    @autobind
    private updateAssistantPartial(text: string) {
        this.setState(prev => {
            const last = prev.messages[prev.messages.length - 1];
            if (last && last.role === ChatRole.ASSISTANT) {
                const updatedLast: ChatMessage = {...last, message_text: text};
                return {...prev, messages: [...prev.messages.slice(0, -1), updatedLast]};
            }
            // If for any reason the last isn't assistant (edge case), append one
            const assistantMsg: ChatMessage = {role: ChatRole.ASSISTANT, message_text: text};
            return {...prev, messages: [...prev.messages, assistantMsg]};
        });
    }

    /**
     * Renders the chat UI: message list and input + send button.
     */
    render(): React.JSX.Element {
        return (
            <div className={styles.tabContainer}>
                <div className={styles.scrollArea} ref={this.listRef}>
                    {this.state.messages.map((m, idx) => (
                        <div key={idx} style={{
                            marginBottom: "0.5rem",
                            display: "flex",
                            justifyContent: m.role === ChatRole.USER ? "flex-end" : "flex-start"
                        }}>
                            <div style={{
                                maxWidth: "80%",
                                padding: "0.5rem 0.75rem",
                                borderRadius: "0.5rem",
                                backgroundColor: m.role === ChatRole.USER ? "#d9edf7" : "#eee"
                            }}>
                                {m.role === ChatRole.ASSISTANT ? (
                                    this.renderAssistantContent(m.message_text)
                                ) : (
                                    <div style={{fontSize: "0.9rem", whiteSpace: "pre-wrap"}}>{m.message_text}</div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
                <div className={styles.toolbarBottom}>
                    <textarea
                        value={this.state.input}
                        onChange={e => this.setState({input: e.target.value})}
                        onKeyDown={e => {
                            if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                if (!this.state.isSending && this.state.input.trim()) {
                                    this.sendMessage();
                                }
                            }
                        }}
                        placeholder="Type your message..."
                        rows={2}
                        style={{
                            flex: 1,
                            padding: "0.5rem",
                            border: "1px solid #ccc",
                            borderRadius: "0.5rem",
                            resize: "vertical",
                            minHeight: "2.5rem",
                            maxHeight: "8rem",
                            fontSize: "1rem",
                        }}
                    />
                    <button
                        onClick={this.sendMessage}
                        disabled={this.state.isSending || !this.state.input.trim()}
                        className="btn btn-primary"
                    >
                        {this.state.isSending ? "Sending..." : "Send"}
                    </button>
                </div>
            </div>
        );
    }

    // Renders assistant content: blocks (HTML + plotly)
    private renderAssistantContent(text: string): React.ReactNode {
        const parsed = tryParseJSONBlocks(text);
        const blocks = parsed.blocks;
        return (
            <div className="ai-content" style={{fontSize: "0.9rem", textAlign: "left"}}>
                {blocks.map((b, i) => {
                    if ((b as AIHtmlBlock).kind === "html") {
                        const html = (b as AIHtmlBlock).content ?? "";
                        return <div key={i} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(html) }} />;
                    }
                    if ((b as AIPlotlyBlock).kind === "plotly") {
                        const p = b as AIPlotlyBlock;
                        return (
                            <figure key={i} className="ai-figure">
                                <div className="ai-figure__media">
                                    <Plot data={p.data} layout={{ autosize: true, ...p.layout }} config={{ displayModeBar: true, responsive: true, ...p.config }} useResizeHandler style={{ width: "100%", height: p.layout?.height ?? 420 }} />
                                </div>
                                {/* If a caption is desired, emit an adjacent HTML block with caption text. */}
                            </figure>
                        );
                    }
                    return null;
                })}
            </div>
        );
    }
}
